import React, { useState, useMemo } from 'react';
import { Box, Stack, CircularProgress, Menu, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { parseSegmentTime } from '../../../../lib/transcriptionHelpers';
import { onClickTimestamp } from '../../../../lib/commonUtils';
import { useTags } from '../../../../hooks/useTags';
import {
  CHAPTERS_TIMESTAMPS_VISIBLE_KEY,
  CHAPTERS_EXPANDED_KEY,
} from '../../../../constants/tags';
import SummaryEmpty from '../summary/SummaryEmpty';
import ChapterToolbar from './ChapterToolbar';
import ChapterAccordion, { compareTimestamps } from './ChapterAccordion';
import { Chapter, ExpandedState } from './types';
import { formatTimestamp, highlightText } from './utils';
import useLimits from '../../../plan/hooks/limits';
import { LanguageCode, top50Languages, translateText } from '../../translate';
import { RootState } from '../../../../reducers';
import { USER_PACKAGE } from '../../../plan/userPackage';

interface ChaptersProps {
  chaptersData: {
    chapters: Chapter[];
  } | null;
  videoRef: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
  allowLocking?: boolean;
  allowSharing?: boolean;
}

const Chapters: React.FC<ChaptersProps> = ({
  chaptersData,
  videoRef,
  allowLocking = true,
  allowSharing = true,
}) => {
  const { getTag, setTag } = useTags();

  // Initialize states with saved preferences
  const [expandedPanels, setExpandedPanels] = useState<ExpandedState>(() => {
    const savedExpanded = getTag(CHAPTERS_EXPANDED_KEY);
    if (chaptersData?.chapters && savedExpanded === 'true') {
      const initialState: ExpandedState = {};
      chaptersData.chapters.forEach((_, index) => {
        initialState[index] = true;
      });
      return initialState;
    }
    return {};
  });

  const [copySuccess, setCopySuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showVisualElements, setShowVisualElements] = useState(() => {
    const savedVisibility = getTag(CHAPTERS_TIMESTAMPS_VISIBLE_KEY);
    return savedVisibility ? savedVisibility === 'true' : false;
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedChapters, setTranslatedChapters] = useState<Chapter[]>([]);
  const guardLimits = useLimits();

  const { package: teamPackage } =
    useSelector((state: RootState) => state.plan.subscription) || {};
  const isFreeUser = teamPackage === USER_PACKAGE.FREE_LOGGEDIN;

  const handleExpandAll = () => {
    if (!chaptersData?.chapters) return;

    // Count expanded panels
    const expandedCount = Object.values(expandedPanels).filter(Boolean).length;
    const totalPanels = chaptersData.chapters.length;
    const majorityExpanded = expandedCount > totalPanels / 2;

    // If majority are expanded, collapse all. Otherwise, expand all
    const newExpandedState: ExpandedState = {};
    chaptersData.chapters.forEach((_, index) => {
      newExpandedState[index] = !majorityExpanded;
    });
    setExpandedPanels(newExpandedState);
    // Save the new expanded state
    setTag({
      key: CHAPTERS_EXPANDED_KEY,
      value: (!majorityExpanded).toString(),
    });
  };

  const handleChange = (panel: number) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedPanels((prev) => {
      const newState = { ...prev, [panel]: isExpanded };
      // Save expanded state based on majority
      const expandedCount = Object.values(newState).filter(Boolean).length;
      const totalPanels = chaptersData?.chapters?.length || 0;
      setTag({
        key: CHAPTERS_EXPANDED_KEY,
        value: (expandedCount > totalPanels / 2).toString(),
      });
      return newState;
    });
  };

  const handleTimeClick = (timestamp: string) => {
    const formattedTime = formatTimestamp(timestamp);
    const time = parseSegmentTime(`[${formattedTime}]`);
    onClickTimestamp(time);
  };

  const handleCopyChapters = () => {
    if (chaptersData?.chapters) {
      const chaptersText = chaptersData.chapters
        .map(
          (chapter: Chapter) =>
            `${chapter.title}\n${chapter.start} - ${chapter.end}\n${chapter.notes}\n`
        )
        .join('\n');
      navigator.clipboard.writeText(chaptersText).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      });
    }
  };

  const handleToggleVisualElements = () => {
    setShowVisualElements((prev) => {
      const newValue = !prev;
      setTag({
        key: CHAPTERS_TIMESTAMPS_VISIBLE_KEY,
        value: newValue.toString(),
      });
      return newValue;
    });
  };

  const handleTranslateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (guardLimits('translateTranscript')) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTranslateChapters = async (targetLang: LanguageCode) => {
    if (!chaptersData?.chapters) return;

    setIsTranslating(true);
    handleClose();

    try {
      const translatedResults = await Promise.all(
        chaptersData.chapters.map(async (chapter) => {
          const translatedTitle = await translateText({
            text: chapter.title,
            targetLang,
          });
          const translatedNotes = await translateText({
            text: chapter.notes,
            targetLang,
          });

          return {
            ...chapter,
            title: translatedTitle,
            notes: translatedNotes,
          };
        })
      );

      setTranslatedChapters(translatedResults);
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const filteredChapters = useMemo(() => {
    if (!chaptersData?.chapters || !searchTerm) return chaptersData?.chapters;
    const regex = new RegExp(searchTerm, 'gi');
    return chaptersData.chapters.filter(
      (chapter: Chapter) =>
        regex.test(chapter.title) || regex.test(chapter.notes)
    );
  }, [chaptersData?.chapters, searchTerm]);

  if (!chaptersData?.chapters) {
    return <SummaryEmpty />;
  }

  // Update isAllExpanded to reflect if majority are expanded
  const isAllExpanded = useMemo(() => {
    if (!chaptersData?.chapters) return false;
    const expandedCount = Object.values(expandedPanels).filter(Boolean).length;
    return expandedCount > chaptersData.chapters.length / 2;
  }, [expandedPanels, chaptersData?.chapters]);

  const currentTime = videoRef.current?.currentTime || 0;

  // Use translated chapters if available, then filter and sort
  const displayedChapters = useMemo(() => {
    const baseChapters =
      translatedChapters.length > 0
        ? translatedChapters
        : filteredChapters || chaptersData?.chapters || [];

    return [...baseChapters].sort((a, b) =>
      compareTimestamps(a.start, b.start)
    );
  }, [translatedChapters, filteredChapters, chaptersData?.chapters]);

  return (
    <Box sx={{ m: 1 }}>
      <ChapterToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        isAllExpanded={isAllExpanded}
        onExpandAll={handleExpandAll}
        onCopyChapters={handleCopyChapters}
        copySuccess={copySuccess}
        showVisualElements={showVisualElements}
        onToggleVisualElements={handleToggleVisualElements}
        onTranslateClick={handleTranslateClick}
        isTranslating={isTranslating}
      />
      {isTranslating ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={0} sx={{ mx: -1 }}>
          {displayedChapters.map((chapter: Chapter, index: number) => (
            <ChapterAccordion
              key={index}
              chapter={chapter}
              index={index}
              expanded={expandedPanels[index] || false}
              searchTerm={searchTerm}
              showVisualElements={showVisualElements}
              onChange={handleChange}
              onTimeClick={handleTimeClick}
              formatTimestamp={formatTimestamp}
              highlightText={highlightText}
              currentTime={currentTime}
              isLocked={
                allowLocking &&
                isFreeUser &&
                index >= Math.ceil(chaptersData.chapters.length / 2)
              }
              allowSharing={allowSharing}
            />
          ))}
        </Stack>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {top50Languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleTranslateChapters(language.code)}
          >
            {language.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default Chapters;
